/**
 * Dónde vive el API.
 *
 * En el navegador la respuesta es «en el mismo origen»: nginx reenvía `/api`
 * y por eso la web compilada sirve igual en todas las instalaciones.
 *
 * Dentro del APK no hay nginx. La app se sirve desde `localhost` del propio
 * teléfono, así que `/api` apuntaría al teléfono mismo. Hace falta una
 * dirección absoluta, y no puede compilarse dentro del binario: el mismo APK
 * tiene que servir para la laptop de una demostración, para un servidor de
 * pruebas y mañana para la nube.
 *
 * De ahí que la dirección se pida una vez y se guarde.
 *
 * ## Y de ahí también que haya un valor por omisión
 *
 * Lo de arriba sigue siendo cierto: la dirección **no se compila dentro del
 * binario**. Lo que sí se compila es cuál proponer cuando todavía no hay
 * ninguna guardada, y eso cambia dos cosas que importan:
 *
 *  · **Un residente no tiene por qué saber qué es un servidor.** Sin esto, las
 *    350 familias de un fraccionamiento abren la app y lo primero que ven es un
 *    campo pidiendo una dirección, con un ejemplo de red local. Eso no es una
 *    pantalla de bienvenida, es un muro.
 *  · **Un revisor de tienda tampoco.** Apple y Google prueban la app desde su
 *    red, sin acceso a ninguna caseta. Una app cuya primera pantalla pide algo
 *    que el revisor no puede contestar no se revisa: se rechaza.
 *
 * Quien monta una caseta sigue pudiendo cambiarla desde el enlace del login, y
 * si la variable no está puesta —la compilación de siempre— el comportamiento
 * es exactamente el de antes.
 */

const CLAVE_SERVIDOR = 'kodica.servidor';

/**
 * Capacitor inyecta este global antes de cargar la app. Se consulta así, y no
 * importando `@capacitor/core`, para que la web no arrastre una dependencia
 * que solo tiene sentido en el móvil.
 */
interface ConCapacitor {
  Capacitor?: { isNativePlatform?: () => boolean };
}

export function esAppNativa(): boolean {
  return (globalThis as ConCapacitor).Capacitor?.isNativePlatform?.() === true;
}

export class DireccionInvalida extends Error {}

/**
 * No se llegó a hablar con nadie: red, puerto equivocado, o un certificado que
 * el sistema no acepta.
 *
 * Se distingue de «ahí responde algo que no es Kodica» porque llevan a sitios
 * opuestos. Lo segundo es una dirección mal escrita. Lo primero, en el APK y
 * contra `https://`, puede ser justo el caso que el emparejamiento existe para
 * resolver: hay un servidor, pero su certificado no lo avala nadie.
 */
export class NoSeConecto extends DireccionInvalida {}

/**
 * Acepta lo que la gente realmente escribe —`192.168.68.52:8081`, con o sin
 * `http://`, con o sin diagonal final— y devuelve algo que `fetch` entienda.
 */
export function normalizarServidor(entrada: string): string {
  const texto = entrada.trim();
  if (!texto) throw new DireccionInvalida('Escribe la dirección del servidor.');

  // Sin esquema es lo normal al teclear una IP. El puerto lo delata: 443 y
  // 8443 son cifrados, y asumir `http://` ahí mandaba al técnico al puerto que
  // no era, con un «no se pudo conectar» que no decía por qué. Lo demás sigue
  // asumiendo `http://`, que es lo que sirve en una demostración.
  const puertoCifrado = /:(443|8443)(\/|$)/.test(texto);
  const conEsquema = /^https?:\/\//i.test(texto)
    ? texto
    : `${puertoCifrado ? 'https' : 'http'}://${texto}`;

  let url: URL;
  try {
    url = new URL(conEsquema);
  } catch {
    throw new DireccionInvalida('Esa dirección no se entiende.');
  }

  if (!url.hostname) throw new DireccionInvalida('Falta la dirección del servidor.');

  // La ruta se descarta a propósito: si alguien pega la URL completa de una
  // pantalla, lo que queremos es el origen, no `/mi-vivienda`.
  return url.origin;
}

export function servidorGuardado(): string | null {
  return localStorage.getItem(CLAVE_SERVIDOR);
}

/**
 * La dirección que trae la compilación, si trae alguna.
 *
 * Se fija con `VITE_SERVIDOR_POR_OMISION` al compilar la web que va dentro del
 * APK o del IPA. Vacía en las compilaciones de siempre, que es lo que deja el
 * comportamiento anterior intacto.
 *
 * Se normaliza igual que lo que teclea una persona: así una variable escrita
 * como `kodica.texelos.com` —sin esquema— no produce una app que no conecta con
 * nada y no dice por qué.
 */
export function servidorPorOmision(): string | null {
  const crudo = import.meta.env.VITE_SERVIDOR_POR_OMISION;
  if (typeof crudo !== 'string' || !crudo.trim()) return null;
  try {
    return normalizarServidor(crudo);
  } catch {
    // Una variable mal escrita no puede tumbar el arranque: se ignora y la app
    // vuelve a preguntar, que es el camino que ya existía.
    return null;
  }
}

/**
 * Con qué servidor se habla: el que eligió esta persona, o el de fábrica.
 *
 * **Es el que hay que consultar en toda la aplicación.** `servidorGuardado` es
 * solo el almacén, y usarlo para decidir si hace falta preguntar la dirección
 * fue lo que hacía que la app preguntara aun trayendo una puesta.
 */
export function servidorEfectivo(): string | null {
  return servidorGuardado() ?? servidorPorOmision();
}

export function guardarServidor(url: string): void {
  localStorage.setItem(CLAVE_SERVIDOR, url);
}

export function olvidarServidor(): void {
  localStorage.removeItem(CLAVE_SERVIDOR);
}

/**
 * Lo que se antepone a cada llamada. Vacío en el navegador —mismo origen— y
 * el servidor elegido en el APK.
 */
export function baseApi(): string {
  if (!esAppNativa()) return '';
  return servidorEfectivo() ?? '';
}

/**
 * Comprueba que del otro lado haya un Kodica vivo antes de dar por buena la
 * dirección. Vale la pena: equivocarse de puerto es el error más común, y sin
 * esto el usuario se entera hasta que falla el login, donde el mensaje que
 * vería es «usuario o contraseña incorrectos».
 */
export async function probarServidor(url: string): Promise<void> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${url}/api/salud`, {
      method: 'GET',
      signal: AbortSignal.timeout(8000),
    });
  } catch {
    throw new NoSeConecto(
      'No se pudo conectar. Revisa que el teléfono esté en la misma red y que la dirección sea correcta.',
    );
  }

  if (!respuesta.ok) {
    throw new DireccionInvalida('Ahí responde algo, pero no es un servidor Kodica.');
  }

  const datos = (await respuesta.json().catch(() => ({}))) as { estado?: string };
  if (datos.estado !== 'ok' && datos.estado !== 'degradado') {
    throw new DireccionInvalida('Ahí responde algo, pero no es un servidor Kodica.');
  }
}
